import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Skeleton, Svg, Text, TextField } from '@hopskipdrive/react-components';
import { formatInTimeZone } from 'date-fns-tz';

import { colorTokens } from '../../../../shared/components/variables';
import { useToast } from '../../../../shared/contexts/ToastContext';
import { estimatedTimeFromNowWithin24Hours } from '../../../../shared/utils/helpers/util';
import { ExpandableNotesContainer } from '../../../components/ExpandableNotesContainer';
import { useLiveSrsAvailabilities } from '../../../hooks/useLiveSrsAvailabilities';
import { useRideNotesQuery } from '../../../hooks/useRideNotesQuery';
import { useRideNotesSubmit } from '../../../hooks/useRideNotesSubmit';
import { RideNotesPayload } from '../../../types/rides';
import { SrsAvatarStatus } from '../SrsAvatarStatus';

import { Heading } from './index';
import { AddNoteContainer, RideNotesContainer } from './RideNotes.styles';

interface RideNotesProps {
  id: string;
  state: 'default' | 'maximized' | 'minimized';
  onClick: () => void;
}

export const RideNotes: FC<RideNotesProps> = ({ id, state, onClick }) => {
  const { rideSlug } = useParams();
  const { data: rideNotesResult, isFetching } = useRideNotesQuery({ rideSlug: rideSlug || '' });
  const [noteContent, setNoteContent] = useState<string>('');
  const { addToast } = useToast();
  const { getSrsLiveStatus } = useLiveSrsAvailabilities();

  const { mutate, isPending } = useRideNotesSubmit({
    rideSlug: rideSlug!,
    successCallback: () => {
      setNoteContent('');
    },
    errorCallback: (error) => {
      addToast({
        severity: 'error',
        message: error.message,
        alertProps: {
          icon: false,
          onClose: undefined,
          style: {
            border: `1px solid ${colorTokens.error.outlineBorder}`,
            background: colorTokens.primary.contrast,
            color: colorTokens.error.main,
          },
        },
      });
    },
  });

  const handleNoteSubmit = () => {
    const notePayload: RideNotesPayload = {
      trip: {
        adminNotes: {
          content: noteContent,
        },
      },
    };

    mutate(notePayload);
  };

  return (
    <ExpandableNotesContainer id={id} state={state}>
      <Box mb={3} sx={{ width: '100%', padding: '12px 12px 0 12px' }}>
        <Heading id={id} state={state} onClick={onClick} />
      </Box>
      <RideNotesContainer>
        {isFetching ? (
          <SkeletonRideNotes />
        ) : (
          <>
            {rideNotesResult?.data?.map((note, index) => (
              <RideNote
                key={index}
                createdAt={note.createdAt}
                createdBy={note.createdBy}
                content={note.content}
                employeeProfilePhotoUrl={note.employeeProfilePhotoUrl}
                employeeId={note.employeeId}
                employeeStatus={getSrsLiveStatus(note.employeeId, note.employeeSrsAvailabilityStatus)}
              />
            ))}
          </>
        )}
      </RideNotesContainer>
      <AddNoteContainer>
        <TextField
          fullWidth
          variant={'outlined'}
          placeholder="| Add an internal note about this ride"
          disabled={false}
          multiline
          maxRows={6}
          value={noteContent}
          onChange={(e) => setNoteContent(e.target.value)}
          style={{ backgroundColor: colorTokens.text.contrast }}
          InputProps={{
            endAdornment: <AddNoteButton onClick={handleNoteSubmit} noteContent={noteContent} isPending={isPending} />,
          }}
        />
      </AddNoteContainer>
    </ExpandableNotesContainer>
  );
};

const RideNote = ({ createdAt, createdBy, content, employeeProfilePhotoUrl, employeeId, employeeStatus }) => {
  return (
    <Box display="flex" alignItems="baseline">
      <Box mr={2}>
        <SrsAvatarStatus
          fullName={createdBy}
          size={33}
          fontSize={14}
          statusSize={12}
          key={employeeId?.toString()}
          status={employeeStatus}
          pictureUrl={employeeProfilePhotoUrl}
        />
      </Box>
      <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="flex-start">
        <Box display="flex" alignItems="center">
          <Box mr={2}>{createdBy === 'Alert Bot' ? <Text>System Bot</Text> : <Text>{createdBy}</Text>}</Box>
          <Box mr={1}>
            <Svg icon="clock" style={{ color: colorTokens.text.secondary }} />
          </Box>
          <Text textColor="secondaryText">
            {formatInTimeZone(new Date(createdAt), 'America/Los_Angeles', 'EEE, M/dd/yyyy | hh:mm aa zzz')}{' '}
            {estimatedTimeFromNowWithin24Hours(createdAt)}
          </Text>
        </Box>

        <Box overflow="auto" sx={{ width: '100%' }}>
          <Text textColor="secondaryText" fontWeight={400} sx={{ whiteSpace: 'pre-line' }}>
            {content}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

interface AddNoteButtonProps {
  onClick: () => void;
  noteContent: string;
  isPending?: boolean;
}

const AddNoteButton: FC<AddNoteButtonProps> = ({ onClick, noteContent, isPending }) => {
  return (
    <Button
      style={{
        paddingLeft: '20px',
        paddingRight: '20px',
        marginTop: 'auto',
      }}
      color="primary"
      startIcon={<Svg icon="note-add" style={{ color: colorTokens.text.contrast }} />}
      sx={{
        '& .MuiButton-startIcon': {
          margin: 0,
        },
      }}
      onClick={onClick}
      disabled={!noteContent.length || isPending}
    >
      {isPending ? 'Adding...' : 'Add Note'}
    </Button>
  );
};

export const SkeletonRideNotes = () => (
  <>
    <Box data-testid="ride-notes-skeleton" display="flex" alignItems="baseline" mb={3}>
      <Box mr={2}>
        <Skeleton variant="circular" width={33} height={33} />
      </Box>
      <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="flex-start">
        <Box display="flex" alignItems="center">
          <Box mr={2}>
            <Skeleton variant="text" width={161} height={28} />
          </Box>
          <Box mr={1}>
            <Skeleton variant="circular" width={19} height={19} />
          </Box>
          <Skeleton variant="text" width={161} height={28} />
        </Box>
        <Box overflow="auto" sx={{ width: '100%' }}>
          <Skeleton variant="text" width={593} height={24} />
        </Box>
      </Box>
    </Box>
    <Box data-testid="ride-notes-skeleton" display="flex" alignItems="baseline" mb={3}>
      <Box mr={2}>
        <Skeleton variant="circular" width={33} height={33} />
      </Box>
      <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="flex-start">
        <Box display="flex" alignItems="center">
          <Box mr={2}>
            <Skeleton variant="text" width={161} height={28} />
          </Box>
          <Box mr={1}>
            <Skeleton variant="circular" width={19} height={19} />
          </Box>
          <Skeleton variant="text" width={161} height={28} />
        </Box>
        <Box overflow="auto" sx={{ width: '100%' }}>
          <Skeleton variant="text" width={593} height={24} />
        </Box>
      </Box>
    </Box>
    <Box data-testid="ride-notes-skeleton" display="flex" alignItems="baseline" mb={3}>
      <Box mr={2}>
        <Skeleton variant="circular" width={33} height={33} />
      </Box>
      <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="flex-start">
        <Box display="flex" alignItems="center">
          <Box mr={2}>
            <Skeleton variant="text" width={161} height={28} />
          </Box>
          <Box mr={1}>
            <Skeleton variant="circular" width={19} height={19} />
          </Box>
          <Skeleton variant="text" width={161} height={28} />
        </Box>
        <Box overflow="auto" sx={{ width: '100%' }}>
          <Skeleton variant="text" width={593} height={24} />
        </Box>
      </Box>
    </Box>
  </>
);
